import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, 
  Plus, 
  Edit2, 
  Trash2, 
  Shield, 
  Mail, 
  Calendar,
  CheckCircle,
  XCircle,
  User,
  ArrowLeft,
  Search,
  UserPlus,
  Key,
  AlertCircle,
  X,
  Save
} from 'lucide-react';

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    permissions: 'full'
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return null;
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Fetch admin users
  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/admins`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setAdmins(data);
        setError('');
      } else if (response.status === 403) {
        setError('You do not have permission to view admin users');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to fetch admin users');
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Create new admin user
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/admins`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const accessLevel = formData.permissions === 'live_dashboard' ? 'Live Dashboard Only' : 'Full Access';
        setSuccess(`Admin user created successfully with ${accessLevel}`);
        setFormData({ name: '', email: '', password: '', role: 'admin', permissions: 'full' });
        setShowAddModal(false);
        fetchAdmins();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create admin user');
      }
    } catch (err) {
      console.error('Error creating admin:', err);
      setError('Network error occurred');
    }
  };

  // Update admin user
  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const updateData = {
        permissions: formData.permissions
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/admins/${selectedAdmin.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        setSuccess('Admin user access level updated successfully');
        setShowEditModal(false);
        setSelectedAdmin(null);
        fetchAdmins();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update admin user');
      }
    } catch (err) {
      console.error('Error updating admin:', err);
      setError('Network error occurred');
    }
  };

  // Delete admin user
  const handleDeleteAdmin = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/admins/${selectedAdmin.id}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        setSuccess('Admin user deleted successfully');
        setShowDeleteModal(false);
        setSelectedAdmin(null);
        fetchAdmins();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete admin user');
      }
    } catch (err) {
      console.error('Error deleting admin:', err);
      setError('Network error occurred');
    }
  };

  // Open edit modal
  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    // Determine permissions based on admin's permissions array
    const hasLiveDashboardOnly = admin.permissions && 
      admin.permissions.length === 1 && 
      admin.permissions[0] === 'live_dashboard.read';
    
    setFormData({
      name: admin.name,
      email: admin.email,
      password: '',
      role: admin.role,
      permissions: hasLiveDashboardOnly ? 'live_dashboard' : 'full'
    });
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (admin) => {
    setSelectedAdmin(admin);
    setShowDeleteModal(true);
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAccessLevelBadge = (admin) => {
    const hasLiveDashboardOnly = admin.permissions && 
      admin.permissions.length === 1 && 
      admin.permissions[0] === 'live_dashboard.read';
    
    if (hasLiveDashboardOnly) {
      return <Badge className="bg-blue-100 text-blue-800 border border-blue-300">Live Dashboard Only</Badge>;
    }
    return <Badge className="bg-purple-100 text-purple-800 border border-purple-300">Full Access</Badge>;
  };

  // Filter admins
  const filteredAdmins = admins.filter(admin => 
    admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                <span className="ml-3 text-gray-600 text-lg">Loading admin users...</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="outline"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-purple-500 to-blue-600 p-4 rounded-2xl shadow-lg">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Admin Users</h1>
                <p className="text-gray-600 mt-1">Manage administrative access and permissions</p>
              </div>
            </div>
            <Button
              onClick={() => {
                setFormData({ name: '', email: '', password: '', role: 'admin', permissions: 'full' });
                setShowAddModal(true);
              }}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg"
              size="lg"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Add Admin User
            </Button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <AlertDescription className="text-red-600 font-medium">{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <AlertDescription className="text-green-600 font-medium">{success}</AlertDescription>
          </Alert>
        )}

        {/* Search Bar */}
        <Card className="mb-6 shadow-lg border-0">
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 text-lg border-2 focus:border-purple-500"
              />
            </div>
          </CardContent>
        </Card>

        {/* Admin Users Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredAdmins.length === 0 ? (
            <Card className="col-span-full shadow-lg border-0">
              <CardContent className="p-12">
                <div className="text-center">
                  <Users className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No admin users found</h3>
                  <p className="text-gray-500">
                    {searchTerm ? "Try adjusting your search" : "Get started by adding your first admin user"}
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            filteredAdmins.map((admin) => (
              <Card key={admin.id} className="shadow-lg border-0 hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-purple-500 to-blue-600"></div>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="bg-gradient-to-br from-purple-100 to-blue-100 p-3 rounded-xl">
                        <User className="w-7 h-7 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{admin.name}</h3>
                        <div className="flex items-center gap-2 text-gray-600 mt-1">
                          <Mail className="w-4 h-4" />
                          <span className="text-sm">{admin.email}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${admin.is_active ? 'bg-green-100' : 'bg-red-100'}`}>
                      {admin.is_active ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                      <span className={`text-xs font-semibold ${admin.is_active ? 'text-green-700' : 'text-red-700'}`}>
                        {admin.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Access Level */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-semibold text-gray-700">Access Level:</span>
                    </div>
                    {getAccessLevelBadge(admin)}
                  </div>

                  {/* Metadata */}
                  <div className="grid grid-cols-2 gap-4 py-4 border-t border-gray-100">
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 mb-1">
                        <Calendar className="w-3 h-3" />
                        <span className="text-xs font-medium">Created</span>
                      </div>
                      <p className="text-sm text-gray-900">{formatDate(admin.created_at)}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 mb-1">
                        <Key className="w-3 h-3" />
                        <span className="text-xs font-medium">Last Login</span>
                      </div>
                      <p className="text-sm text-gray-900">{formatDate(admin.last_login)}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t border-gray-100">
                    <Button
                      onClick={() => openEditModal(admin)}
                      variant="outline"
                      className="flex-1 border-2 border-purple-200 hover:bg-purple-50 hover:border-purple-300"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit Access
                    </Button>
                    <Button
                      onClick={() => openDeleteModal(admin)}
                      variant="outline"
                      className="border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Add Admin Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl shadow-2xl">
              <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-2xl">
                    <UserPlus className="w-6 h-6 mr-3 text-purple-600" />
                    Add New Admin User
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowAddModal(false)}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleCreateAdmin} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-sm font-semibold text-gray-700">Full Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="John Smith"
                        className="mt-2 h-11"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-semibold text-gray-700">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="admin@umoonaopalmine.com.au"
                        className="mt-2 h-11"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="password" className="text-sm font-semibold text-gray-700">Password *</Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="Strong password (min 8 characters)"
                        className="mt-2 h-11"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="permissions" className="text-sm font-semibold text-gray-700">Access Level *</Label>
                      <Select 
                        value={formData.permissions}
                        onValueChange={(value) => setFormData({...formData, permissions: value})}
                      >
                        <SelectTrigger className="mt-2 h-11">
                          <SelectValue placeholder="Select access level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">
                            <div className="flex flex-col py-1">
                              <span className="font-semibold">Full Access</span>
                              <span className="text-xs text-gray-500">All admin features and permissions</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="live_dashboard">
                            <div className="flex flex-col py-1">
                              <span className="font-semibold">Live Dashboard Only</span>
                              <span className="text-xs text-gray-500">View and manage today's tours only</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Alert className="border-blue-200 bg-blue-50">
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800 text-sm">
                      <strong>Full Access:</strong> Complete control over all admin features<br/>
                      <strong>Live Dashboard Only:</strong> Can only view and manage today's tour bookings
                    </AlertDescription>
                  </Alert>

                  <div className="flex gap-3 pt-4">
                    <Button 
                      type="submit" 
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 h-12 text-base font-semibold"
                    >
                      <UserPlus className="w-5 h-5 mr-2" />
                      Create Admin User
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowAddModal(false)}
                      className="h-12 px-8"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Edit Admin Modal */}
        {showEditModal && selectedAdmin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg shadow-2xl">
              <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-2xl">
                    <Edit2 className="w-6 h-6 mr-3 text-purple-600" />
                    Edit Access Level
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedAdmin(null);
                    }}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleUpdateAdmin} className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Editing access for:</p>
                    <p className="text-lg font-bold text-gray-900">{selectedAdmin.name}</p>
                    <p className="text-sm text-gray-600">{selectedAdmin.email}</p>
                  </div>

                  <div>
                    <Label htmlFor="edit-permissions" className="text-sm font-semibold text-gray-700">Access Level *</Label>
                    <Select 
                      value={formData.permissions}
                      onValueChange={(value) => setFormData({...formData, permissions: value})}
                    >
                      <SelectTrigger className="mt-2 h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full">
                          <div className="flex flex-col py-1">
                            <span className="font-semibold">Full Access</span>
                            <span className="text-xs text-gray-500">All admin features and permissions</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="live_dashboard">
                          <div className="flex flex-col py-1">
                            <span className="font-semibold">Live Dashboard Only</span>
                            <span className="text-xs text-gray-500">View and manage today's tours only</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button 
                      type="submit" 
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 h-12 font-semibold"
                    >
                      <Save className="w-5 h-5 mr-2" />
                      Save Changes
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setShowEditModal(false);
                        setSelectedAdmin(null);
                      }}
                      className="h-12 px-8"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedAdmin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md shadow-2xl">
              <CardHeader className="border-b bg-red-50">
                <CardTitle className="flex items-center text-2xl text-red-700">
                  <AlertCircle className="w-6 h-6 mr-3" />
                  Delete Admin User
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <Alert className="border-red-200 bg-red-50">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <AlertDescription className="text-red-800 font-medium">
                      This action cannot be undone. This will permanently delete the admin user account.
                    </AlertDescription>
                  </Alert>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">You are about to delete:</p>
                    <p className="text-lg font-bold text-gray-900">{selectedAdmin.name}</p>
                    <p className="text-sm text-gray-600">{selectedAdmin.email}</p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button 
                      onClick={handleDeleteAdmin}
                      className="flex-1 bg-red-600 hover:bg-red-700 h-12 font-semibold"
                    >
                      <Trash2 className="w-5 h-5 mr-2" />
                      Delete User
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setShowDeleteModal(false);
                        setSelectedAdmin(null);
                      }}
                      className="h-12 px-8"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
